import { createAsyncThunk } from '@reduxjs/toolkit';
import { changeUserData, createUser, getUserById, linkUserData } from '../../api/user';
import { User } from '@capacitor-firebase/authentication';
import i18n from 'src/i18n';
import { normalizeAuthName } from 'src/shared';
import { parseAttributionParams } from 'src/utils/parseAttributionParams';
import { RootState } from '..';
import { patchUserAppLang } from 'src/api/languages';

export const loadUserThunk = createAsyncThunk('user/setUser', async (id: string) => {
  const data = await getUserById(id);
  return data;
});

export const updateUserAppLang = createAsyncThunk('user/updateUserAppLang', async (lang: string) => {
  const data = await patchUserAppLang(lang);
  return data;
});

type Args = { authUser: User };
export const createUserProfile = createAsyncThunk<any, Args, { state: RootState; rejectValue: string }>(
  'user/createUserProfile',
  async ({ authUser }: Args, { getState, rejectWithValue }) => {
    try {
      if (!authUser) throw new Error('Missing Firebase user from SignInResult');

      const state = getState();
      const { signUpCountry, signUpUSState, signUpDoB, signUpName } = state.auth;

      const userName = normalizeAuthName({
        authFullDisplayName: authUser.displayName ?? '',
        enteredFirstName: signUpName?.firstName ?? '',
        enteredLastName: signUpName?.lastName ?? '',
      });

      const payload = {
        ...userName,
        email: authUser.email ?? '',
        country: signUpCountry,
        us_state: signUpUSState,
        dob: signUpDoB,
        attributionParams: parseAttributionParams(),
      };

      const userResponse = await createUser(authUser.uid, payload, i18n.language);
      return userResponse;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Failed to create user profile');
    }
  }
);

export const updateUserProfile = createAsyncThunk<any, Args, { state: RootState; rejectValue: string }>(
  'user/updateUserProfile',
  async ({ authUser }: Args, { getState, rejectWithValue, dispatch }) => {
    try {
      if (!authUser) throw new Error('Missing Firebase user from SignInResult');

      const state = getState();
      const { onBoardingUserId, newOnBoardingAnswers, newOnBoardingData } = state.auth;

      if (authUser.uid !== onBoardingUserId && newOnBoardingAnswers) {
        const attributionParams = parseAttributionParams();
        await changeUserData(
          {
            onBoardingAnswers: newOnBoardingAnswers,
            ...(newOnBoardingData && { onboarding_key: newOnBoardingData.id }),
            specialist_order: null,
            is_viewed_specialist_list: false,
            ...attributionParams,
          },
          authUser.uid
        );
      }
      const userRes = await dispatch(loadUserThunk(authUser.uid)).unwrap();

      return userRes;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Failed to update user profile');
    }
  }
);

export const linkUserProfile = createAsyncThunk<any, Args, { state: RootState; rejectValue: string }>(
  'user/linkUserProfile',
  async ({ authUser }: Args, { getState, rejectWithValue, dispatch }) => {
    try {
      if (!authUser) throw new Error('Missing Firebase user from SignInResult');

      const state = getState();
      const { signUpName, signUpCountry, signUpUSState, signUpDoB } = state.auth;

      const userName = normalizeAuthName({
        authFullDisplayName: authUser.displayName ?? '',
        enteredFirstName: signUpName?.firstName ?? '',
        enteredLastName: signUpName?.lastName ?? '',
      });

      const payload = {
        display_name: userName.firstName,
        last_name: userName.lastName,
        country: signUpCountry,
        us_state: signUpUSState,
        dob: signUpDoB,
        ...(authUser.email && { email: authUser.email }),
      };

      await linkUserData(payload, authUser.uid, i18n.language);

      return payload;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Failed to link user profile');
    }
  }
);
